import React, { useState, useEffect } from "react";
import { leadPricingAPI, categoryAPI } from "../../services/api";
import { TableSkeleton } from "../../components/skeletons";
import { renderIcon } from "./TaxonomyManager";

const GREEN = "#1B5C3A",
  GOLD = "#F5A623",
  CHAR = "#1a1a1a",
  MUTED = "#6b7280",
  BORDER = "#e5e7eb";

export default function LeadPricingManager() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  const [filter, setFilter] = useState("");
  const [financialRate, setFinancialRate] = useState("");
  const [savingFinancial, setSavingFinancial] = useState(false);

  const loadData = () => {
    setLoading(true);
    Promise.all([
      leadPricingAPI.list(),
      leadPricingAPI.getFinancialRate()
    ])
      .then(([rList, rFinancial]) => {
        setRules(rList.data.data || []);
        setFinancialRate(String(rFinancial.data.rate ?? ""));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveFinancialRate = async () => {
    if (financialRate === "" || isNaN(Number(financialRate))) {
      return alert("Please enter a valid rate");
    }
    setSavingFinancial(true);
    try {
      await leadPricingAPI.updateFinancialRate(Number(financialRate));
      alert("Global financial lead price updated successfully!");
    } catch (e) {
      alert(e.response?.data?.message || "Failed to update financial lead price");
    } finally {
      setSavingFinancial(false);
    }
  };

  const handleEdit = (ruleId, field, value) => {
    const newEdits = { ...(edits[ruleId] || {}), [field]: value };

    // Always set outCityRate to 50% of localRate when localRate changes
    if (field === "localRate" && value) {
      const localVal = parseFloat(value);
      newEdits.outCityRate = String(Number((localVal * 0.5).toFixed(2)));
    }

    setEdits((prev) => ({ ...prev, [ruleId]: newEdits }));
  };

  const handleCancelEdit = (ruleId) => {
    setEdits((prev) => {
      const n = { ...prev };
      delete n[ruleId];
      return n;
    });
  };

  const handleSave = async (rule) => {
    const changes = edits[rule._id] || {};
    const scope = changes.leadReceivingFrom ?? rule.leadReceivingFrom ?? "home_state";
    setSaving((prev) => ({ ...prev, [rule._id]: true }));
    try {
      await leadPricingAPI.update(rule._id, {
        leadReceivingFrom: scope,
        localRate: parseFloat(changes.localRate ?? rule.localRate),
        outCityRate: scope === "home_state"
          ? (changes.outCityRate !== undefined ? parseFloat(changes.outCityRate) : rule.outCityRate)
          : null,
        bulkRate: changes.bulkRate !== undefined ? (changes.bulkRate ? parseFloat(changes.bulkRate) : null) : rule.bulkRate,
      });
      setEdits((prev) => {
        const n = { ...prev };
        delete n[rule._id];
        return n;
      });
      loadData();
    } catch (e) {
      alert(e.response?.data?.message || "Save failed");
    }
    setSaving((prev) => ({ ...prev, [rule._id]: false }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this pricing rule?")) return;
    await leadPricingAPI.remove(id);
    loadData();
  };

  const filtered = filter
    ? rules.filter((r) => {
        const q = filter.toLowerCase();
        return (
          (r.categoryId?.name || "").toLowerCase().includes(q) ||
          (r.subCategoryId?.name || "").toLowerCase().includes(q) ||
          (r.brandId?.name || "").toLowerCase().includes(q)
        );
      })
    : rules;

  if (loading) return <TableSkeleton rows={6} columns={5} style={{ padding: 24 }} />;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "var(--font-serif)",
              fontSize: 26,
              fontWeight: 700,
              color: CHAR,
            }}
          >
            Lead Pricing
          </h1>
          <p style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>
            {rules.length} pricing rules ·{" "}
            <a
              href="/admin/taxonomy"
              style={{ color: GREEN, fontWeight: 600, textDecoration: "none" }}
            >
              Add new via Taxonomy Manager →
            </a>
          </p>
        </div>
        <input
          className="form-input"
          placeholder="Filter by category, sub, brand..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ width: 260, fontSize: 13 }}
        />
      </div>

      {/* Global Financial Lead Price Card */}
      <div style={{
        background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
        border: "1px solid #bfdbfe",
        borderRadius: 14,
        padding: "16px 20px",
        marginBottom: 20,
        display: "flex",
        flexWrap: "wrap",
        gap: 16,
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1e3a8a", margin: 0 }}>
            🏦 Global Financial Lead Price
          </h3>
          <p style={{ fontSize: 12, color: "#1e40af", marginTop: 4, marginBottom: 0 }}>
            This flat price applies to all financial vendor lead unlocks across all categories and cities.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#1e3a8a" }}>₹</span>
          <input
            type="number"
            min="0"
            value={financialRate}
            onChange={(e) => setFinancialRate(e.target.value)}
            onWheel={(e) => e.target.blur()}
            style={{
              width: 80,
              padding: "6px 10px",
              border: "1.5px solid #93c5fd",
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 700,
              textAlign: "right",
              color: "#1e3a8a",
            }}
          />
          <button
            onClick={handleSaveFinancialRate}
            disabled={savingFinancial}
            style={{
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              transition: "background 0.2s",
            }}
          >
            {savingFinancial ? "Saving..." : "Update Rate"}
          </button>
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          border: `1px solid ${BORDER}`,
          borderRadius: 14,
          overflow: "hidden",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              {[
                "Category",
                "Subcategory",
                "Brand",
                "Receiving From",
                "Local/Flat (₹)",
                "Out-City (₹)",
                "Bulk (₹)",
                "Actions",
              ].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: "12px 14px",
                    textAlign: h.includes("₹") ? "right" : "left",
                    fontSize: 11,
                    fontWeight: 700,
                    color: MUTED,
                    textTransform: "uppercase",
                    borderBottom: `2px solid ${BORDER}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  style={{ padding: 40, textAlign: "center", color: MUTED }}
                >
                  No pricing rules found
                </td>
              </tr>
            ) : (
              filtered.map((r) => {
                const e = edits[r._id] || {};
                const isDirty = Object.keys(e).length > 0;
                const currentScope = e.leadReceivingFrom ?? r.leadReceivingFrom ?? "home_state";
                return (
                  <tr
                    key={r._id}
                    style={{
                      borderBottom: `1px solid ${BORDER}`,
                      background: isDirty ? "#fefce8" : "#fff",
                    }}
                  >
                    <td style={{ padding: "10px 14px", fontSize: 13 }}>
                      {renderIcon(r.categoryId?.icon)}
                      {r.categoryId?.name || "—"}
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 13 }}>
                      {renderIcon(r.subCategoryId?.icon)}
                      {r.subCategoryId?.name || "—"}
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 13 }}>
                      {renderIcon(r.brandId?.icon)}
                      {r.brandId?.name || (
                        <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                          All
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <select
                        value={currentScope}
                        onChange={(ev) =>
                          handleEdit(r._id, "leadReceivingFrom", ev.target.value)
                        }
                        style={{
                          padding: "4px 8px",
                          border: `1px solid ${isDirty ? GOLD : BORDER}`,
                          borderRadius: 6,
                          fontSize: 12,
                          background: "#fff",
                        }}
                      >
                        <option value="home_city">Home City</option>
                        <option value="home_state">Home State</option>
                        <option value="nation_wide">Nation Wide</option>
                      </select>
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "right" }}>
                      <input
                        type="number"
                        min="0"
                        value={e.localRate ?? r.localRate}
                        onChange={(ev) =>
                          handleEdit(r._id, "localRate", ev.target.value)
                        }
                        onWheel={(e) => e.target.blur()} 
                        style={{
                          width: 70,
                          padding: "4px 8px",
                          border: `1px solid ${isDirty ? GOLD : BORDER}`,
                          borderRadius: 6,
                          textAlign: "right",
                          fontSize: 14,
                          fontWeight: 700,
                          fontFamily: "var(--font-serif)",
                          color: GREEN,
                        }}
                      />
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "right" }}>
                      {currentScope === "home_state" ? (
                        <input
                          type="number"
                          min="0"
                          value={e.outCityRate ?? r.outCityRate}
                          onChange={(ev) =>
                            handleEdit(r._id, "outCityRate", ev.target.value)
                          }
                          onWheel={(e) => e.target.blur()} 
                          style={{
                            width: 70,
                            padding: "4px 8px",
                            border: `1px solid ${isDirty ? GOLD : BORDER}`,
                            borderRadius: 6,
                            textAlign: "right",
                            fontSize: 14,
                            fontWeight: 700,
                            fontFamily: "var(--font-serif)",
                            color: GOLD,
                          }}
                        />
                      ) : (
                        <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "right" }}>
                      <input
                        type="number"
                        min="0"
                        value={e.bulkRate ?? r.bulkRate ?? ""}
                        onChange={(ev) =>
                          handleEdit(r._id, "bulkRate", ev.target.value)
                        }
                        onWheel={(e) => e.target.blur()} 
                        style={{
                          width: 70,
                          padding: "4px 8px",
                          border: `1px solid ${isDirty ? GOLD : BORDER}`,
                          borderRadius: 6,
                          textAlign: "right",
                          fontSize: 13,
                          color: MUTED,
                        }}
                      />
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {isDirty && (
                          <>
                            <button
                              onClick={() => handleSave(r)}
                              disabled={saving[r._id]}
                              style={{
                                background: GREEN,
                                color: "#fff",
                                border: "none",
                                borderRadius: 6,
                                padding: "5px 12px",
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                            >
                              {saving[r._id] ? "..." : "Save"}
                            </button>
                            <button
                              onClick={() => handleCancelEdit(r._id)}
                              style={{
                                background: "#f3f4f6",
                                color: MUTED,
                                border: `1px solid ${BORDER}`,
                                borderRadius: 6,
                                padding: "5px 12px",
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(r._id)}
                          style={{
                            background: "#fff",
                            border: `1px solid ${BORDER}`,
                            borderRadius: 6,
                            padding: "5px 8px",
                            fontSize: 11,
                            cursor: "pointer",
                            color: "#dc2626",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
